<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES, VOICE_CALL_STATUS } from '../constants';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';

const LABEL_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS',
  [VOICE_CALL_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
  [VOICE_CALL_STATUS.CANCELED]: 'CONVERSATION.VOICE_CALL.CANCELED_CALL',
  [VOICE_CALL_STATUS.FAILED]: 'CONVERSATION.VOICE_CALL.FAILED_CALL',
};

const ICON_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'i-ph-phone-call',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.FAILED]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.CANCELED]: 'i-ph-phone-x',
  [VOICE_CALL_STATUS.MISSED]: 'i-ph-phone-x',
};

const BG_COLOR_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'bg-n-teal-9',
  [VOICE_CALL_STATUS.RINGING]: 'bg-n-teal-9 animate-pulse',
  [VOICE_CALL_STATUS.COMPLETED]: 'bg-n-slate-11',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'bg-n-ruby-9',
  [VOICE_CALL_STATUS.FAILED]: 'bg-n-ruby-9',
  [VOICE_CALL_STATUS.CANCELED]: 'bg-n-slate-11',
  [VOICE_CALL_STATUS.MISSED]: 'bg-n-ruby-9',
};

const FAILURE_STATUSES = new Set([
  VOICE_CALL_STATUS.NO_ANSWER,
  VOICE_CALL_STATUS.FAILED,
  VOICE_CALL_STATUS.MISSED,
]);

const TERMINAL_STATUSES = new Set([
  VOICE_CALL_STATUS.COMPLETED,
  VOICE_CALL_STATUS.NO_ANSWER,
  VOICE_CALL_STATUS.FAILED,
  VOICE_CALL_STATUS.CANCELED,
  VOICE_CALL_STATUS.MISSED,
]);

const { contentAttributes, messageType } = useMessageContext();

const data = computed(() => contentAttributes.value?.data);
const status = computed(() => data.value?.status?.toString());
const meta = computed(() => data.value?.meta || {});

const isOutbound = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isFailureState = computed(() => FAILURE_STATUSES.has(status.value));

const labelKey = computed(() => {
  if (LABEL_MAP[status.value]) return LABEL_MAP[status.value];
  if (status.value === VOICE_CALL_STATUS.RINGING) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.OUTGOING_CALL'
      : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
  }
  return isFailureState.value
    ? 'CONVERSATION.VOICE_CALL.MISSED_CALL'
    : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
});

const iconName = computed(() => {
  if (ICON_MAP[status.value]) return ICON_MAP[status.value];
  return isOutbound.value ? 'i-ph-phone-outgoing' : 'i-ph-phone-incoming';
});

const bgColor = computed(() => BG_COLOR_MAP[status.value] || 'bg-n-teal-9');

// Epoch seconds are persisted server-side on conversation.additional_attributes
// and mirrored onto content_attributes.data.meta by Voice::CallStatus::Manager.
const epochToDate = seconds =>
  typeof seconds === 'number' && Number.isFinite(seconds)
    ? new Date(seconds * 1000)
    : null;

const formatClock = seconds => {
  const date = epochToDate(seconds);
  if (!date) return '';
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = totalSeconds => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

// Live clock that only ticks while the call is in progress. A plain Date.now()
// computed wouldn't re-evaluate on its own since nothing reactive changes —
// so we push a fresh timestamp into a ref each second.
const nowSeconds = ref(Math.floor(Date.now() / 1000));
let tickHandle = null;

const startTicking = () => {
  if (tickHandle !== null) return;
  tickHandle = setInterval(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000);
  }, 1000);
};

const stopTicking = () => {
  if (tickHandle !== null) {
    clearInterval(tickHandle);
    tickHandle = null;
  }
};

watch(
  status,
  value => {
    if (value === VOICE_CALL_STATUS.IN_PROGRESS) startTicking();
    else stopTicking();
  },
  { immediate: true }
);

onUnmounted(stopTicking);

const liveDurationSeconds = computed(() => {
  const startedAt = meta.value.callStartedAt;
  if (!startedAt) return null;
  return Math.max(0, nowSeconds.value - startedAt);
});

function terminalLabelKey(s) {
  switch (s) {
    case VOICE_CALL_STATUS.NO_ANSWER:
      return 'CONVERSATION.VOICE_CALL.LIFECYCLE.NO_ANSWER';
    case VOICE_CALL_STATUS.MISSED:
      return 'CONVERSATION.VOICE_CALL.LIFECYCLE.MISSED';
    case VOICE_CALL_STATUS.CANCELED:
      return 'CONVERSATION.VOICE_CALL.LIFECYCLE.CANCELED';
    case VOICE_CALL_STATUS.FAILED:
      return 'CONVERSATION.VOICE_CALL.LIFECYCLE.FAILED';
    default:
      return 'CONVERSATION.VOICE_CALL.LIFECYCLE.ENDED';
  }
}

// The stacked history of events we have evidence for on this call. Each row
// has a label (i18n key) and a value (already-formatted time or duration).
// We render every row we have data for, so a completed call shows three rows
// (Arrived → Answered → Ended), a canceled ring shows two (Arrived → Canceled),
// and a ringing call shows just the arrival row.
//
// Keys arrive camelCased because MessageList.vue runs useCamelCase(deep:true)
// before the message reaches this component; the backend stores them snake_cased
// on content_attributes.data.meta.
const timelineEntries = computed(() => {
  const {
    ringingAt,
    acceptedAt,
    callStartedAt: startedAt,
    callEndedAt: endedAt,
    callDuration: duration,
  } = meta.value;

  const entries = [];

  if (ringingAt) {
    entries.push({
      labelKey: 'CONVERSATION.VOICE_CALL.LIFECYCLE.ARRIVED',
      value: formatClock(ringingAt),
    });
  }

  const answerStamp = acceptedAt || startedAt;
  if (answerStamp) {
    entries.push({
      labelKey: 'CONVERSATION.VOICE_CALL.LIFECYCLE.ANSWERED',
      value: formatClock(answerStamp),
    });
  }

  if (status.value === VOICE_CALL_STATUS.IN_PROGRESS && answerStamp) {
    entries.push({
      labelKey: 'CONVERSATION.VOICE_CALL.LIFECYCLE.DURATION',
      value: formatDuration(liveDurationSeconds.value || 0),
    });
  }

  if (TERMINAL_STATUSES.has(status.value) && endedAt) {
    entries.push({
      labelKey: terminalLabelKey(status.value),
      value: formatClock(endedAt),
    });
    if (Number.isFinite(duration) && duration > 0) {
      entries.push({
        labelKey: 'CONVERSATION.VOICE_CALL.LIFECYCLE.DURATION',
        value: formatDuration(duration),
      });
    }
  }

  return entries;
});

// Fallback line when no timeline data is available yet (e.g. immediately
// after a ringing message is created before the dashboard has the meta block).
const hasTimeline = computed(() => timelineEntries.value.length > 0);
const fallbackSubtextKey = computed(() => {
  if (status.value === VOICE_CALL_STATUS.IN_PROGRESS) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.THEY_ANSWERED'
      : 'CONVERSATION.VOICE_CALL.YOU_ANSWERED';
  }
  if (isFailureState.value) return 'CONVERSATION.VOICE_CALL.NO_ANSWER';
  return 'CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET';
});
</script>

<template>
  <BaseBubble class="p-0 border-none" hide-meta>
    <div class="flex overflow-hidden flex-col w-full max-w-xs">
      <div class="flex gap-3 items-start p-3 w-full">
        <div
          class="flex justify-center items-center rounded-full size-10 shrink-0 mt-0.5"
          :class="bgColor"
        >
          <Icon
            class="size-5"
            :icon="iconName"
            :class="{
              'text-n-slate-1': status === VOICE_CALL_STATUS.COMPLETED,
              'text-white': status !== VOICE_CALL_STATUS.COMPLETED,
            }"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow gap-0.5">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ $t(labelKey) }}
          </span>

          <div v-if="hasTimeline" class="flex flex-col gap-0.5 mt-1">
            <div
              v-for="entry in timelineEntries"
              :key="entry.labelKey + entry.value"
              class="flex items-center justify-between gap-2 text-xs"
            >
              <span class="text-n-slate-11">{{ $t(entry.labelKey) }}</span>
              <span class="text-n-slate-12 font-medium tabular-nums">
                {{ entry.value }}
              </span>
            </div>
          </div>

          <span v-else class="text-xs text-n-slate-11">
            {{ $t(fallbackSubtextKey) }}
          </span>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
